
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { heroSlides, coreValueProps, partners } from '../data';

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40, filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const scaleIn = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]); // Parallax for hero

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <main className="font-geist text-slate-900 bg-white overflow-hidden">
      <style>{`
        .pause-on-hover:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden flex items-center bg-slate-950">
        <div className="absolute inset-0 z-0 bg-slate-950">
          <AnimatePresence mode="popLayout">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              {/* Eager load the first image, lazy for others */}
              <img
                src={heroSlides[currentSlide]}
                alt="Medical Facility"
                className="w-full h-full object-cover brightness-[0.85]"
                loading={currentSlide === 0 ? "eager" : "lazy"}
                fetchPriority={currentSlide === 0 ? "high" : "auto"}
              />
            </motion.div>
          </AnimatePresence>

          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/40 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent z-10 pointer-events-none"></div>
        </div>

        <motion.div
          className="relative z-20 max-w-7xl mx-auto px-6 md:px-8 w-full h-full flex items-center"
          style={{ y: y1 }}
        >
          <motion.div
            className="max-w-4xl pt-20"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-8">
              <span className="h-0.5 w-16 bg-accent"></span>
              <span className="text-accent text-xs font-bold uppercase tracking-[0.5em] text-white">Since 2013</span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl lg:text-7xl font-sans-jakarta font-extrabold text-white mb-8 leading-[1.1] tracking-tight drop-shadow-lg"
            >
              Dr CG Du Plessis <br />
              <span className="text-white/80">& Partners</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-slate-100 text-lg md:text-xl font-light max-w-xl leading-relaxed mb-12 border-l border-white/40 pl-8 drop-shadow-md"
            >
              Your trusted General Practitioners in Fourways. Providing comprehensive, patient-centered medical care.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-6">
              <a
                href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-accent text-white px-10 py-5 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-primary transition-all shadow-[0_0_40px_-10px_rgba(13,148,136,0.5)] hover:shadow-white/20 flex items-center justify-center gap-3 group"
              >
                <span className="material-symbols-outlined text-xl group-hover:scale-110 transition-transform">calendar_month</span>
                Book Appointment
              </a>
              <Link to="/services" className="px-10 py-5 rounded-full text-xs font-bold uppercase tracking-widest text-white border border-white/30 hover:bg-white hover:text-primary transition-all flex items-center justify-center gap-3 backdrop-blur-sm">
                Our Services
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Partners Marquee Section */}
      <section className="py-12 border-b border-slate-100 bg-white overflow-hidden relative z-20">
        <div className="max-w-7xl mx-auto mb-10 px-6 text-center md:text-left">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400">Integrated Medical Partners</p>
        </div>

        <div className="absolute top-0 bottom-0 left-0 w-24 md:w-40 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
        <div className="absolute top-0 bottom-0 right-0 w-24 md:w-40 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

        <div className="relative flex overflow-x-hidden group">
          <div className="flex animate-scroll whitespace-nowrap pause-on-hover">
            {/* Using a key with index is safe here as the list is static */}
            {[...partners, ...partners, ...partners, ...partners].map((partner, index) => (
              <div key={`${partner.name}-${index}`} className="flex items-center justify-center mx-10 md:mx-16 shrink-0">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  loading="lazy"
                  className="h-16 md:h-20 w-auto object-contain filter grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-pointer mix-blend-multiply"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-32 px-6 md:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image Column */}
          <motion.div
            className="relative order-2 lg:order-1"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={scaleIn}
          >
            <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl relative z-10 bg-slate-100">
              <img
                src="https://ik.imagekit.io/6dlukcyok/Medical%20bucket/Chrissy.avif"
                alt="Dr Chrissy Du Plessis"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative Border */}
            <motion.div
              className="absolute top-8 left-8 w-full h-full rounded-[2.5rem] border-2 border-slate-100 -z-0 hidden md:block"
              initial={{ opacity: 0, x: -20, y: -20 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.8 }}
            />
          </motion.div>

          {/* Content Column */}
          <motion.div
            className="order-1 lg:order-2"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.span variants={fadeInUp} className="text-accent font-bold text-sm uppercase tracking-[0.4em] mb-8 block">Welcome to our Practice</motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-6xl font-sans-jakarta font-bold text-primary mb-10 leading-[1.1] tracking-tight">
              Personalized care from <br /><span className="italic text-teal-600">Dr. Chrissy & Team</span>.
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-8 text-slate-600 font-light text-xl leading-loose">
              <p>
                "At Dr CG Du Plessis & Partners, we believe in taking the time to truly understand our patients. Our practice is built on the foundation of thorough consultations and long-term doctor-patient relationships."
              </p>
              <p>
                I started this practice with a simple vision: that our medical doctors adopt a holistic healthcare approach that incorporates the different physical, mental, emotional and social aspects of your life to ensure your complete overall health and wellbeing.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-12">
              <Link to="/about" className="inline-flex items-center gap-3 text-primary font-bold text-sm uppercase tracking-widest hover:text-accent transition-colors border-b border-primary hover:border-accent pb-2">
                More about us <span className="material-symbols-outlined text-lg">arrow_forward</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Services Section - Clean Icon Cards Design */}
      <section className="py-24 px-6 md:px-8 bg-soft-gray border-t border-slate-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <div className="inline-block px-4 py-2 rounded-full border border-teal-100 bg-teal-50 mb-6">
              <span className="text-accent font-bold text-[10px] uppercase tracking-[0.2em]">Core Services</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-sans-jakarta font-bold text-slate-900">Healthcare Services</h2>
          </motion.div>

          {/* Grid Layout - 3 columns for clean cards */}
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10"
          >
            {coreValueProps.map((item) => (
              <motion.div
                key={item.id}
                className="group bg-white p-10 rounded-[2rem] border border-slate-100 hover:border-white hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 flex flex-col h-full relative overflow-hidden"
                whileHover={{ y: -5 }}
              >
                {/* Top Accent Gradient Line on Hover */}
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${item.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>

                {/* Icon - Large and Colorful */}
                <div className="mb-8">
                  <span className={`material-symbols-outlined text-7xl bg-gradient-to-br ${item.gradient} bg-clip-text text-transparent transform group-hover:scale-110 transition-transform duration-500 origin-left drop-shadow-sm`}>
                    {item.icon}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-sans-jakarta font-bold text-slate-900 mb-8 tracking-tight">
                  {item.title}
                </h3>

                {/* Features List */}
                <ul className="space-y-5 mb-10 flex-grow">
                  {item.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-4 text-slate-600 font-medium">
                      <span className="material-symbols-outlined text-teal-500 text-xl shrink-0 mt-0.5">check_circle</span>
                      <span className="leading-tight">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Link */}
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 text-primary font-bold text-sm hover:gap-4 transition-all duration-300 group/link"
                >
                  Explore Now
                  <span className="material-symbols-outlined text-lg bg-slate-100 rounded-full w-6 h-6 flex items-center justify-center group-hover/link:bg-primary group-hover/link:text-white transition-colors">arrow_forward</span>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Mobile View All Button */}
          <div className="mt-12 text-center md:hidden">
            <Link to="/services" className="inline-block border border-slate-200 px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest text-slate-500 hover:bg-slate-50 hover:text-primary transition-all">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-8 bg-white relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="relative overflow-hidden bg-primary rounded-[2.5rem] md:rounded-[3.5rem] p-10 md:p-16 lg:py-24 text-white shadow-2xl text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={scaleIn}
          >
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
              <div className="absolute top-[-30%] left-[-15%] w-[80%] aspect-square bg-accent rounded-full blur-[2px] opacity-100"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-primary/40 to-transparent"></div>
            </div>

            <div className="relative z-10 flex flex-col items-center max-w-4xl mx-auto">
              <motion.span variants={fadeInUp} className="text-[10px] font-bold uppercase tracking-[0.4em] mb-6 opacity-80">Book Now</motion.span>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl lg:text-7xl font-sans-jakarta font-extrabold mb-10 leading-[1.1] tracking-tight">
                Prioritize your health <br /> today.
              </motion.h2>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a
                  href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 bg-white px-10 py-5 rounded-full text-primary font-bold text-xs tracking-[0.15em] uppercase hover:bg-teal-500 hover:text-white transition-all shadow-xl transform hover:-translate-y-1"
                >
                  <span className="material-symbols-outlined text-xl group-hover:scale-110 transition-transform">calendar_month</span>
                  Book Appointment
                </a>
                <Link to="/team" className="group flex items-center gap-4 border-2 border-white px-10 py-5 rounded-full text-white font-bold text-xs tracking-[0.15em] uppercase hover:bg-white hover:text-primary transition-all transform hover:-translate-y-1">
                  <span className="material-symbols-outlined text-xl">groups</span>
                  Meet the Team
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Home;
